import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    // NEURO HORIZON FORK (#188 — DO NOT drop on re-sync). Sold-out options are blocked from
    // selection (stock Horizon allows picking them): capture-phase click guard + arrow-key skip.
    // The paired halves are the tooltip span in snippets/variant-main-picker.liquid and the
    // styles/pop-gate CSS in snippets/neuro-variant-picker-styles.liquid.
    this.addEventListener('click', this.#blockSoldOutClick, true);
    this.addEventListener('keydown', this.#skipSoldOutOnArrow);
    this.#resizeObserver.observe(this);
  }

  /** @type {WeakMap<Element, number>} */
  #tipTimeouts = new WeakMap();

  /**
   * NEURO (#188): whether a radio input is a blocked, sold-out option. On the current
   * single-option products data-option-available="false" means the variant is truly sold out.
   * On a future multi-option product it would mean "unavailable with the other selections" and
   * blocking it could deadlock option switching — revisit before such a product ships (see
   * docs/design-system/components/VariantPicker.md).
   *
   * Swatch-type options (label --has-swatch, incl. the product-card
   * SwatchesVariantPickerComponent subclass, which inherits these listeners via
   * super.connectedCallback) are excluded: they keep stock Horizon treatment — the whole
   * blocking contract (peeled sticker, tooltip) is authored for BUTTON groups only, and the
   * swatch surfaces render no tooltip to explain a refused pick.
   * @param {EventTarget | null} target
   * @returns {HTMLInputElement | null}
   */
  #asBlockedOption(target) {
    if (!(target instanceof HTMLElement)) return null;
    const input = target instanceof HTMLInputElement ? target : target.querySelector?.('input[type="radio"]');
    if (!(input instanceof HTMLInputElement)) return null;
    if (input.closest('label')?.classList.contains('variant-option__button-label--has-swatch')) return null;
    return input.dataset.optionAvailable === 'false' ? input : null;
  }

  /**
   * NEURO (#188): show the sold-out tooltip for ~1.6s (the tap/click path; hover and
   * keyboard focus are handled in CSS alone).
   * @param {HTMLInputElement} input
   */
  #openSoldOutTip(input) {
    const label = input.closest('label');
    if (!label) return;
    label.classList.add('neuro-tip-open');
    clearTimeout(this.#tipTimeouts.get(label));
    this.#tipTimeouts.set(
      label,
      window.setTimeout(() => label.classList.remove('neuro-tip-open'), 1600)
    );
  }

  /**
   * NEURO (#188): capture-phase click guard — cancels the pick before the radio checks,
   * so no change event, no Section Rendering fetch, no analytics.
   * @param {MouseEvent} event
   */
  #blockSoldOutClick = (event) => {
    const label = event.target instanceof HTMLElement ? event.target.closest('label') : null;
    const blocked = this.#asBlockedOption(label);
    if (!blocked) return;
    event.preventDefault();
    this.#openSoldOutTip(blocked);
  };

  /**
   * NEURO (#188): arrow keys skip sold-out options (the native radio-group roving would land
   * on them and commit the pick). Only intercepts groups that contain a blocked option; screen
   * readers still reach the options when browsing (aria-disabled + "- Sold out" aria-label).
   * @param {KeyboardEvent} event
   */
  #skipSoldOutOnArrow = (event) => {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
        ? -1
        : 0;
    if (step === 0) return;

    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    // swatch-type groups keep stock arrow behavior (same exclusion as #asBlockedOption)
    if (input.closest('label')?.classList.contains('variant-option__button-label--has-swatch')) return;

    const fieldsetIndex = Number(input.dataset.fieldsetIndex);
    const radios = this.#radios[fieldsetIndex];
    if (!radios || !radios.some((radio) => radio.dataset.optionAvailable === 'false')) return;

    event.preventDefault();
    const from = radios.indexOf(input);
    for (let i = 1; i <= radios.length; i++) {
      // + radios.length * i keeps the index non-negative for the leftward (step -1) walk
      const candidate = radios[(from + step * i + radios.length * i) % radios.length];
      if (!candidate || candidate === input) break;
      if (candidate.dataset.optionAvailable === 'false') continue;
      candidate.checked = true;
      candidate.focus();
      candidate.dispatchEvent(new Event('change', { bubbles: true }));
      break;
    }
  };

  /**
   * NEURO (#188): restore the previous selection after a blocked pick slipped past the
   * click/keydown guards (runs before updateSelectedOption, so #checkedIndices[i][0] is still
   * the previous pick). Re-checking the previous radio auto-unchecks the blocked one.
   * @param {HTMLInputElement} blocked
   */
  #revertBlockedPick(blocked) {
    const fieldsetIndex = Number(blocked.dataset.fieldsetIndex);
    const radios = this.#radios[fieldsetIndex];
    const previousIndex = this.#checkedIndices[fieldsetIndex]?.[0];
    const previous = previousIndex != null ? radios?.[previousIndex] : undefined;

    if (previous && previous !== blocked) {
      previous.checked = true;
    } else {
      blocked.checked = false;
    }
    this.#openSoldOutTip(blocked);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  // NEURO HORIZON FORK from 70c27a8 (#10, the variant-tracking family — DO NOT drop on re-sync).
  // Sent by hand because the Shopify-bundled Amplitude SDK ignores the `dataAttributePrefix`
  // autocapture config. Upstream 70c27a8 removes this method AND its call in variantChanged(); keep both.
  // The Liquid emitter (the data-amp-track-* attrs in snippets/variant-main-picker.liquid) is the paired
  // half. Guard: tests/e2e/local/variant-tracking.spec.js + live Amplitude ('Variant Selected').
  /** @param {HTMLInputElement | HTMLOptionElement} selectedOption */
  #trackVariantSelected(selectedOption) {
    const amp = /** @type {{ track?: Function } | undefined} */ (
      /** @type {any} */ (window).amplitude
    );
    if (typeof amp?.track !== 'function') return;

    const ds = selectedOption.dataset;
    if (!ds.ampTrackOptionName) return;

    amp.track('Variant Selected', {
      option_name: ds.ampTrackOptionName,
      option_value: ds.ampTrackOptionValue,
      variant_id: ds.variantId || null,
      product_handle: ds.ampTrackProductHandle,
      product_title: ds.ampTrackProductTitle,
      product_vendor: ds.ampTrackProductVendor,
      product_type: ds.ampTrackProductType,
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    // NEURO HORIZON FORK (#188 — DO NOT drop on re-sync). Safety net behind the click/keydown
    // guards: any change that still lands on a sold-out option is reverted before tracking,
    // Section Rendering, and URL updates. A blocked pick is a non-event. Routed through
    // #asBlockedOption so the swatch exclusion holds on this path too.
    const blockedPick = selectedOption instanceof HTMLInputElement ? this.#asBlockedOption(selectedOption) : null;
    if (blockedPick) {
      this.#revertBlockedPick(blockedPick);
      return;
    }

    // NEURO HORIZON FORK (#188): stamp the pop gate. The CSS keyframe in
    // snippets/neuro-variant-picker-styles.liquid requires data-neuro-picked alongside :checked,
    // and only a real user pick sets it — server-rendered DOM (page load, quick-add open,
    // Section-Rendering morphs) never carries the attribute, so none of those replay the pop.
    // Value must be truthy ('true', not ''): morph.js's onBeforeUpdate preserve-list only
    // carries a truthy oldValue across a morph, and a mid-keyframe strip would snap the pop.
    // The stale stamp is cleared from every other label first, so exactly one label carries
    // it at a time (re-picking a previously-picked option restarts its pop via the attribute
    // toggling back on, and the one-pick-one-pop contract doesn't lean on :has(:checked) alone).
    const pickedLabel = selectedOption.closest('label');
    if (pickedLabel) {
      for (const stale of this.querySelectorAll('label[data-neuro-picked]')) {
        if (stale !== pickedLabel) stale.removeAttribute('data-neuro-picked');
      }
      pickedLabel.setAttribute('data-neuro-picked', 'true');
    }

    // NEURO HORIZON FORK (#10) — keep this call; see #trackVariantSelected above (DO NOT drop on re-sync).
    this.#trackVariantSelected(selectedOption);

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
